import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetModerationReportQuery, useReviewModerationReportMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Image from '../../../components/Image';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';
import { usePermission } from '../../../hooks/usePermission';
import { useLanguage } from '../../../context/LanguageContext';
import { formatDateTime } from '../../../utils/helpers';
import { statusBadgeClass, actionLabels } from './Reports';

const ReportDetails: React.FC = () => {
  const navigate = useNavigate();
  const { type, id } = useParams<{ type: string; id: string }>();
  const { t, lang } = useLanguage();
  const { can } = usePermission();

  const { data, isLoading } = useGetModerationReportQuery({ type, id }, { skip: !type || !id });
  const [reviewReport, { isLoading: isSubmitting }] = useReviewModerationReportMutation();

  const report = data?.data;

  const [status, setStatus] = useState('reviewed');
  const [action, setAction] = useState('none');
  const [note, setNote] = useState('');

  // Prefill the form with the decision already on record, if any.
  useEffect(() => {
    if (report) {
      setStatus(report.status === 'pending' ? 'reviewed' : report.status);
      setAction(report.action_taken || 'none');
      setNote(report.admin_note || '');
    }
  }, [report]);

  const canReview = can('user-reports.review');
  const canAction = can('user-reports.action');

  const actionOptions = [
    { value: 'none', label: t('No action — record decision only') },
    { value: 'warn_user', label: t('Issue warning to the reported user') },
    { value: 'suspend_user', label: t('Suspend the reported account') },
    ...(type === 'ad' ? [{ value: 'reject_ad', label: t('Remove the reported ad') }] : []),
  ];

  const handleSubmit = async () => {
    const actionLabel = actionOptions.find((o) => o.value === action)?.label;
    const confirmed = await confirmAction(
      t('Confirm Review Decision'),
      `${t('Status')}: ${t(status)}\n${t('Action')}: ${actionLabel}`,
      t('Yes, submit')
    );
    if (!confirmed) return;

    try {
      const result = await reviewReport({ type, id, status, action, note: note || undefined }).unwrap();
      showSuccess(result.message || t('Report reviewed successfully!'));
      navigate('/admin/user-reports');
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to review report. Please try again.'));
    }
  };

  if (isLoading) return <PageLoader />;
  if (!report) {
    return <div className="app-container container-xxl py-10 text-center text-muted">{t('Report not found')}</div>;
  }

  const person = (u: any, fallback: string) => (
    <div className="d-flex align-items-center">
      <div className="symbol symbol-50px me-3">
        {u?.picture ? (
          <Image src={u.picture} alt={u?.name} type="avatar" className="w-50px h-50px rounded-circle object-fit-cover" />
        ) : (
          <span className="symbol-label bg-light-primary text-primary fw-bold">{(u?.name || 'U').charAt(0).toUpperCase()}</span>
        )}
      </div>
      <div className="d-flex flex-column">
        <span
          className="fw-bold text-gray-800 text-hover-primary cursor-pointer"
          onClick={() => u?.id && navigate(`/admin/users/${u.id}`)}
        >
          {u?.name || fallback}
        </span>
        <span className="text-muted fs-7">{u?.email}</span>
        {u?.acc_status === 0 && <span className="badge badge-light-danger fs-8 mt-1 w-fit-content">{t('Suspended')}</span>}
      </div>
    </div>
  );

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {report.type === 'ad' ? t('Ad Report') : t('User Report')} #{report.id}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary cursor-pointer" onClick={() => navigate('/admin/user-reports')}>{t('Reports & Complaints')}</span>
                </li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">#{report.id}</li>
              </ul>
            </div>
            <button className="btn btn-light-primary" onClick={() => navigate('/admin/user-reports')}>
              <i className={`fas ${lang === 'ar' ? 'fa-arrow-right' : 'fa-arrow-left'} me-2`}></i>{t('Back to Reports')}
            </button>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="row g-5">
              {/* Report content */}
              <div className="col-lg-7">
                <div className="card card-flush mb-5">
                  <div className="card-header pt-6">
                    <div className="card-title m-0"><h3 className="fw-bold m-0">{t('Report Details')}</h3></div>
                    <div className="card-toolbar">
                      <span className={`badge ${statusBadgeClass(report.status)} text-capitalize fs-7`}>{t(report.status)}</span>
                    </div>
                  </div>
                  <div className="card-body pt-4">
                    <div className="row g-5 mb-6">
                      <div className="col-md-6">
                        <span className="text-muted fw-semibold fs-7 d-block mb-2">{t('Reporter')}</span>
                        {person(report.reporter, t('Unknown User'))}
                      </div>
                      <div className="col-md-6">
                        <span className="text-muted fw-semibold fs-7 d-block mb-2">{t('Reported User')}</span>
                        {person(report.accused, t('Unknown User'))}
                      </div>
                    </div>

                    {report.ad && (
                      <div
                        className="d-flex align-items-center bg-light-primary rounded p-4 mb-6 cursor-pointer"
                        onClick={() => navigate(`/admin/ads/${report.ad.id}`)}
                      >
                        <div className="symbol symbol-60px me-4">
                          {report.ad.image ? (
                            <Image src={report.ad.image} alt={report.ad.title} className="w-60px h-60px rounded object-fit-cover" />
                          ) : (
                            <span className="symbol-label bg-primary text-white"><i className="fas fa-bullhorn fs-3 text-white"></i></span>
                          )}
                        </div>
                        <div className="d-flex flex-column">
                          <span className="text-muted fs-8">{t('Reported Ad')}</span>
                          <span className="fw-bold text-gray-800 fs-5">{report.ad.title}</span>
                          <span className="text-muted fs-8">
                            {report.ad.ad_id || `#${report.ad.id}`} · {t('Status')}: {t(report.ad.status)}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="mb-5">
                      <span className="text-muted fw-semibold fs-7 d-block mb-1">{t('Reason')}</span>
                      <span className="fw-bold text-gray-800 fs-5">{report.reason || '—'}</span>
                    </div>

                    <div className="mb-5">
                      <span className="text-muted fw-semibold fs-7 d-block mb-1">{t('Details')}</span>
                      <span className="text-gray-700 fs-6">{report.details || '—'}</span>
                    </div>

                    <div className="mb-5">
                      <span className="text-muted fw-semibold fs-7 d-block mb-1">{t('Submitted')}</span>
                      <span className="text-gray-700 fs-6">{formatDateTime(report.created_at)}</span>
                    </div>

                    {Array.isArray(report.images) && report.images.length > 0 && (
                      <div>
                        <span className="text-muted fw-semibold fs-7 d-block mb-2">{t('Evidence')}</span>
                        <div className="d-flex flex-wrap gap-3">
                          {report.images.map((img: string, idx: number) => (
                            <a key={idx} href={img} target="_blank" rel="noreferrer">
                              <Image src={img} alt={`${t('Evidence')} ${idx + 1}`} className="w-100px h-100px rounded object-fit-cover border border-gray-200" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {report.accused_history && (
                  <div className="card card-flush">
                    <div className="card-header pt-6">
                      <div className="card-title m-0"><h3 className="fw-bold m-0">{t('Reported User History')}</h3></div>
                    </div>
                    <div className="card-body pt-4 d-flex flex-wrap gap-10">
                      <div className="d-flex flex-column">
                        <span className="fs-2 fw-bold text-gray-800">{report.accused_history.ad_reports}</span>
                        <span className="text-muted fs-7">{t('Reports against their ads')}</span>
                      </div>
                      <div className="d-flex flex-column">
                        <span className="fs-2 fw-bold text-gray-800">{report.accused_history.user_reports}</span>
                        <span className="text-muted fs-7">{t('Reports against them')}</span>
                      </div>
                      <div className="d-flex flex-column">
                        <span className="fs-2 fw-bold text-gray-800">{report.accused_history.violations}</span>
                        <span className="text-muted fs-7">{t('Recorded violations')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Review panel */}
              <div className="col-lg-5">
                <div className="card card-flush">
                  <div className="card-header pt-6">
                    <div className="card-title m-0"><h3 className="fw-bold m-0">{t('Review & Decision')}</h3></div>
                  </div>
                  <div className="card-body pt-4">
                    {report.reviewed_at && (
                      <div className="alert alert-light-info d-flex flex-column mb-6">
                        <span className="fw-bold text-gray-800">
                          {t('Last reviewed by')} {report.reviewed_by || t('System')} · {formatDateTime(report.reviewed_at)}
                        </span>
                        <span className="text-gray-700 fs-7">
                          {t('Action Taken')}: {report.action_taken && report.action_taken !== 'none' ? t(actionLabels[report.action_taken]) : t('No Action')}
                        </span>
                        {report.admin_note && <span className="text-gray-600 fs-7 mt-1">{report.admin_note}</span>}
                      </div>
                    )}

                    {!canReview ? (
                      <div className="text-muted">{t('You do not have permission to review reports.')}</div>
                    ) : (
                      <>
                        <div className="mb-5">
                          <label className="form-label fw-semibold">{t('Decision')}</label>
                          <select className="form-select form-select-solid" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="reviewed">{t('Reviewed — looked at, no decision yet')}</option>
                            <option value="resolved">{t('Accept report — valid, action taken')}</option>
                            <option value="dismissed">{t('Reject report — invalid or unfounded')}</option>
                          </select>
                        </div>

                        <div className="mb-5">
                          <label className="form-label fw-semibold">{t('Action')}</label>
                          <select
                            className="form-select form-select-solid"
                            value={action}
                            onChange={(e) => setAction(e.target.value)}
                            disabled={!canAction}
                          >
                            {actionOptions.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          {!canAction && (
                            <div className="form-text">{t('You do not have permission to take action on reports.')}</div>
                          )}
                        </div>

                        <div className="mb-5">
                          <label className="form-label fw-semibold">{t('Admin Note')}</label>
                          <textarea
                            className="form-control form-control-solid"
                            rows={4}
                            placeholder={t('Why was this decision made? (visible to admins only)')}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                          />
                        </div>

                        <button className="btn btn-base w-100" onClick={handleSubmit} disabled={isSubmitting}>
                          {isSubmitting ? t('Submitting...') : t('Submit Decision')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDetails;
